import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Play, X, MessageCircle, Volume2 } from "lucide-react";
import { WHATSAPP_URL, styleWhatsApp } from "@/lib/scroll";
import { SHORT_FORM, LONG_FORM } from "@/data/videos";

function Frame({ item, aspect, onOpen, testid }) {
  const videoRef = useRef(null);

  const preview = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  };
  const stopPreview = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testid}
      onClick={() => onOpen(item)}
      onMouseEnter={preview}
      onMouseLeave={stopPreview}
      className="group relative block w-full cursor-pointer overflow-hidden rounded-2xl border border-white/8 bg-[#0E0E12] text-left"
    >
      <div className={aspect === "vertical" ? "aspect-[9/16]" : "aspect-video"}>
        <video
          ref={videoRef}
          poster={item.poster || undefined}
          muted
          loop
          playsInline
          preload={item.poster ? "none" : "metadata"}
          className="h-full w-full object-cover"
        >
          <source src={item.src} type="video/mp4" />
          <source src={item.src.replace(".mp4", ".webm")} type="video/webm" />
        </video>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/25 opacity-80 transition-opacity duration-500 group-hover:opacity-95" />
      <span className="mono-label pointer-events-none absolute left-3 top-3 rounded-md bg-black/55 px-2.5 py-1 text-[9px] text-white backdrop-blur-md">
        {item.title}
      </span>
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span className="flex h-12 w-12 scale-75 items-center justify-center rounded-full border border-white/30 bg-black/45 opacity-0 backdrop-blur-md transition-all duration-400 group-hover:scale-100 group-hover:opacity-100">
          <Play className="ml-0.5 h-4 w-4 fill-white text-white" />
        </span>
      </span>
      <span className="pointer-events-none absolute inset-x-3 bottom-3 flex items-end justify-between gap-2">
        <span className="font-display text-base font-bold uppercase leading-tight tracking-wide text-white drop-shadow sm:text-lg">
          {item.style}
        </span>
        <span className="mb-0.5 h-[3px] w-8 shrink-0 rounded-full bg-gradient-to-r from-[#FF3B30] to-[#FF9500] transition-all duration-500 group-hover:w-14" />
      </span>
    </motion.div>
  );
}

const FEATURED = [
  SHORT_FORM.find((v) => v.title === "Adidas Samba ADs"),
  SHORT_FORM.find((v) => v.title === "Talking Head 04"),
  SHORT_FORM.find((v) => v.title === "Viral Gym X Anime"),
].filter(Boolean);

function FeaturedCard({ item, onOpen, testid }) {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const inView = useInView(ref, { amount: 0.45 });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) v.play().catch(() => {});
    else v.pause();
  }, [inView]);

  const vertical = item.src.includes("/short/");

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testid}
      onClick={() => onOpen(item)}
      className="group relative shrink-0 cursor-pointer snap-start overflow-hidden rounded-2xl border border-white/8 bg-[#0E0E12] text-left"
    >
      <video
        ref={videoRef}
        poster={item.poster || undefined}
        muted
        loop
        playsInline
        preload={item.poster ? "none" : "metadata"}
        className={`h-[400px] sm:h-[460px] object-cover ${vertical ? "aspect-[9/16]" : "aspect-video"}`}
      >
        <source src={item.src} type="video/mp4" />
        <source src={item.src.replace(".mp4", ".webm")} type="video/webm" />
      </video>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/25 opacity-80 transition-opacity duration-500 group-hover:opacity-95" />
      <span className="mono-label pointer-events-none absolute left-3 top-3 rounded-md bg-black/55 px-2.5 py-1 text-[9px] text-white backdrop-blur-md">
        {item.style}
      </span>
      <span className="pointer-events-none absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 backdrop-blur-md">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#FF3B30]" />
        <span className="mono-label text-[8px] text-white">AUTO-PREVIEW</span>
      </span>
      <span className="pointer-events-none absolute inset-x-3 bottom-3 flex items-end justify-between gap-2">
        <span className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-white drop-shadow sm:text-xl">
          {item.title}
        </span>
        <span className="mb-1 h-[3px] w-10 shrink-0 rounded-full bg-gradient-to-r from-[#FF3B30] to-[#FF9500] transition-all duration-500 group-hover:w-16" />
      </span>
    </motion.div>
  );
}

export default function Work() {
  const [active, setActive] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const modalVideoRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    setSoundOn(false);
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [active]);

  const enableSound = (e) => {
    e.stopPropagation();
    setSoundOn(true);
    const v = modalVideoRef.current;
    if (v) {
      v.muted = false;
      v.play().catch(() => {});
    }
  };

  return (
    <section id="work" className="relative border-t border-white/5 py-24 lg:py-32">
      <div className="wrap">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-14 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="eyebrow mono-label text-[11px] text-[#FF3B30]">Selected work</p>
            <h2 className="mt-5 font-display text-4xl font-black uppercase leading-[0.9] tracking-tight sm:text-5xl lg:text-6xl">
              One style. <span className="text-gradient">One killer cut.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
            Hand-picked edits — the single strongest cut from every style I work in. Hover to
            preview, click to watch.
          </p>
        </motion.div>

        {/* featured reel */}
        <div className="mb-4 flex items-center gap-4">
          <h3 className="font-display text-2xl font-bold uppercase tracking-wide">Featured reel</h3>
          <span className="mono-label rounded-md border border-white/10 px-2.5 py-1 text-[9px] text-zinc-500">
            Auto-previews · swipe
          </span>
        </div>
        <div
          data-testid="featured-reel-strip"
          className="no-scrollbar relative -mx-6 mb-16 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2 lg:-mx-10 lg:px-10"
        >
          {FEATURED.map((f, i) => (
            <FeaturedCard key={f.src} item={f} onOpen={setActive} testid={`featured-card-${i + 1}`} />
          ))}
        </div>

        {/* short-form */}
        <div className="mb-4 flex items-center gap-4">
          <h3 className="font-display text-2xl font-bold uppercase tracking-wide">Short-form</h3>
          <span className="mono-label rounded-md border border-white/10 px-2.5 py-1 text-[9px] text-zinc-500">
            9:16 · {SHORT_FORM.length} styles
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4" data-testid="short-form-gallery-grid">
          {SHORT_FORM.map((s, i) => (
            <Frame key={s.src} item={s} aspect="vertical" onOpen={setActive} testid={`short-form-card-${i + 1}`} />
          ))}
        </div>

        {/* long-form */}
        <div className="mb-4 mt-16 flex items-center gap-4">
          <h3 className="font-display text-2xl font-bold uppercase tracking-wide">Long-form</h3>
          <span className="mono-label rounded-md border border-white/10 px-2.5 py-1 text-[9px] text-zinc-500">
            16:9 · {LONG_FORM.length} styles
          </span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="long-form-gallery-grid">
          {LONG_FORM.map((l, i) => (
            <Frame key={l.src} item={l} aspect="wide" onOpen={setActive} testid={`long-form-card-${i + 1}`} />
          ))}
          <motion.a
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="work-cta-card"
            className="group relative flex aspect-video flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border border-dashed border-white/15 bg-[#0E0E12]/60 text-center transition-colors duration-500 hover:border-[#FF3B30]/50"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#FF3B30] to-[#FF9500] transition-transform duration-500 group-hover:scale-110">
              <MessageCircle className="h-5 w-5 text-white" />
            </span>
            <p className="font-display text-2xl font-bold uppercase tracking-wide">Your video next?</p>
            <p className="mono-label text-[9px] text-zinc-500">Message me to claim the slot</p>
          </motion.a>
        </div>
      </div>

      {/* player modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="video-preview-modal"
            onClick={() => setActive(null)}
            onKeyDown={(e) => e.key === "Escape" && setActive(null)}
            tabIndex={0}
            ref={(el) => el?.focus()}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 outline-none sm:p-8"
          >
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
            <motion.div
              initial={{ scale: 0.92, y: 24, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.94, y: 12, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl"
              data-testid="video-player-container"
            >
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0E0E12]">
                <video
                  key={active.src}
                  ref={modalVideoRef}
                  poster={active.poster || undefined}
                  controls
                  autoPlay
                  muted={!soundOn}
                  loop
                  playsInline
                  data-testid="video-player-element"
                  className="max-h-[70vh] w-full bg-black object-contain"
                >
                  <source src={active.src} type="video/mp4" />
                  <source src={active.src.replace(".mp4", ".webm")} type="video/webm" />
                </video>
                {!soundOn && (
                  <button
                    data-testid="video-unmute-button"
                    onClick={enableSound}
                    className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/20 bg-black/60 px-4 py-2 backdrop-blur-md transition-colors hover:border-[#FF3B30]/60"
                  >
                    <Volume2 className="h-4 w-4 text-[#FF9500]" />
                    <span className="mono-label text-[10px] text-white">Tap for sound</span>
                  </button>
                )}
                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/8 p-5">
                  <div>
                    <p className="mono-label text-[10px] text-[#FF9500]">{active.title}</p>
                    <p className="mt-1.5 font-display text-xl font-bold uppercase tracking-wide">
                      {active.style}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href={styleWhatsApp(active.style, active.title)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="video-modal-whatsapp-button"
                      className="btn-primary !px-5 !py-2.5 text-sm"
                    >
                      <MessageCircle className="h-4 w-4" />
                      I want this style
                    </a>
                    <button
                      data-testid="video-modal-footer-close-button"
                      onClick={() => setActive(null)}
                      className="btn-ghost !px-5 !py-2.5 text-sm"
                    >
                      <X className="h-4 w-4" />
                      Close
                    </button>
                  </div>
                </div>
              </div>
              <button
                data-testid="video-preview-close-button"
                onClick={() => setActive(null)}
                aria-label="Close player"
                className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-md transition-colors hover:border-white/50"
              >
                <X className="h-5 w-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}